import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import {
  MessageSquare, X, Send, Phone, Calendar as CalendarIcon,
  ArrowLeft, CheckCircle2, MapPin, Clock, Sparkles, Loader2,
  Mic, MicOff, Volume2, VolumeX,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { CLINIC, SERVICES } from "@/lib/clinic";
import { buildWhatsAppLink } from "@/components/site/WhatsApp";
import { useVoice, classifyIntent } from "@/hooks/useVoice";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIME_SLOTS = ["09:00", "10:00", "11:00", "12:00", "13:00", "15:00", "16:00", "17:00", "18:00"];

const SERVICE_OPTIONS = SERVICES.map((s) => s.name);

const GREETING = "Hi there! 👋 I'm your City Smiles assistant. How can I help you today?";

// -----------------------------------------------------------------------------
// Message bubble primitives
// -----------------------------------------------------------------------------

function BotBubble({ children, delay = 0 }) {
  return (
    <div
      className="flex items-end gap-2 fade-up"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="w-7 h-7 rounded-full bg-[#2D4A3E] text-white flex items-center justify-center text-[10px] font-semibold shrink-0 mb-0.5">
        CS
      </div>
      <div className="max-w-[85%] bg-white border border-stone-200 rounded-2xl rounded-bl-md px-4 py-2.5 text-sm text-[#1C221F] shadow-sm">
        {children}
      </div>
    </div>
  );
}

function UserBubble({ children }) {
  return (
    <div className="flex justify-end fade-up">
      <div className="max-w-[80%] bg-[#2D4A3E] text-white rounded-2xl rounded-br-md px-4 py-2.5 text-sm">
        {children}
      </div>
    </div>
  );
}

function QuickPill({ children, onClick, testId, tone = "default", icon: Icon }) {
  const tones = {
    default: "bg-white border-stone-300 text-[#1C221F] hover:border-[#2D4A3E] hover:bg-[#2D4A3E]/5",
    primary: "bg-[#2D4A3E] border-[#2D4A3E] text-white hover:bg-[#21362D]",
    wa: "bg-[#25D366] border-[#25D366] text-white hover:bg-[#1FBA5A]",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 rounded-full border text-xs font-medium px-3 py-1.5 transition-colors ${tones[tone]}`}
    >
      {Icon && <Icon className="w-3.5 h-3.5" strokeWidth={1.75} />}
      {children}
    </button>
  );
}

// -----------------------------------------------------------------------------
// Booking wizard steps
// -----------------------------------------------------------------------------

function ChatBookingWizard({ onCancel, onDone }) {
  const [step, setStep] = useState("name"); // name → phone → service → date → time → review
  const [form, setForm] = useState({ name: "", phone: "", service: "", date: undefined, time: "" });
  const [submitting, setSubmitting] = useState(false);

  const next = (patch) => {
    setForm((f) => ({ ...f, ...patch }));
    const order = ["name", "phone", "service", "date", "time", "review"];
    const i = order.indexOf(step);
    setStep(order[Math.min(i + 1, order.length - 1)]);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/appointments`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        service: form.service,
        preferred_date: format(form.date, "yyyy-MM-dd"),
        preferred_time: form.time,
        message: "Booked via chat assistant",
      });
      toast.success("Appointment requested! We'll call to confirm.");
      onDone();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg : detail;
      toast.error(msg || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-3">
      <BotBubble>
        {step === "name" && <>Let&apos;s get you booked. What&apos;s your <b>name</b>?</>}
        {step === "phone" && <>Great, thanks {form.name.split(" ")[0]}. What&apos;s the <b>best phone number</b> to reach you?</>}
        {step === "service" && <>Which <b>treatment</b> are you booking for?</>}
        {step === "date" && <>Pick your <b>preferred date</b>.</>}
        {step === "time" && <>And a <b>time</b> that works for you?</>}
        {step === "review" && (
          <>
            All set — please confirm:
            <div className="mt-2 text-xs text-[#57605B] space-y-1">
              <div><b className="text-[#1C221F]">Name:</b> {form.name}</div>
              <div><b className="text-[#1C221F]">Phone:</b> {form.phone}</div>
              <div><b className="text-[#1C221F]">Service:</b> {form.service}</div>
              <div><b className="text-[#1C221F]">When:</b> {form.date && format(form.date, "d MMM yyyy")} at {form.time}</div>
            </div>
          </>
        )}
      </BotBubble>

      {/* Input controls per step */}
      {step === "name" && (
        <InlineTextInput
          placeholder="Your full name"
          testId="chat-input-name"
          onSubmit={(v) => v.trim().length >= 2 && next({ name: v.trim() })}
        />
      )}
      {step === "phone" && (
        <InlineTextInput
          placeholder="e.g. 99969 27080"
          type="tel"
          testId="chat-input-phone"
          onSubmit={(v) => v.trim().length >= 7 && next({ phone: v.trim() })}
        />
      )}
      {step === "service" && (
        <div className="flex flex-wrap gap-1.5">
          {SERVICE_OPTIONS.map((s) => (
            <QuickPill key={s} testId={`chat-service-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => next({ service: s })}>
              {s}
            </QuickPill>
          ))}
        </div>
      )}
      {step === "date" && (
        <div className="flex justify-start">
          <div className="bg-white border border-stone-200 rounded-2xl p-2 shadow-sm">
            <Calendar
              mode="single"
              selected={form.date}
              onSelect={(d) => d && next({ date: d })}
              disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
              initialFocus
              data-testid="chat-calendar"
            />
          </div>
        </div>
      )}
      {step === "time" && (
        <div className="flex flex-wrap gap-1.5">
          {TIME_SLOTS.map((t) => (
            <QuickPill key={t} testId={`chat-time-${t}`} onClick={() => next({ time: t })}>{t}</QuickPill>
          ))}
        </div>
      )}
      {step === "review" && (
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            disabled={submitting}
            onClick={submit}
            data-testid="chat-confirm-booking"
            className="rounded-full bg-[#2D4A3E] hover:bg-[#21362D] text-white h-9 px-5 text-xs"
          >
            {submitting ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Booking…</> : (<><CheckCircle2 className="w-3.5 h-3.5 mr-1.5" strokeWidth={2} />Confirm booking</>)}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onCancel}
            className="rounded-full h-9 px-4 text-xs"
            data-testid="chat-cancel-booking"
          >
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
}

function InlineTextInput({ placeholder, type = "text", onSubmit, testId }) {
  const [val, setVal] = useState("");
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(val);
      }}
      className="flex items-center gap-2 pl-9"
    >
      <Input
        type={type}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        autoFocus
        className="h-10 rounded-full bg-white border-stone-300 text-sm"
      />
      <Button
        type="submit"
        size="sm"
        data-testid={`${testId}-submit`}
        className="rounded-full h-10 w-10 p-0 bg-[#2D4A3E] hover:bg-[#21362D] text-white shrink-0"
      >
        <Send className="w-4 h-4" strokeWidth={1.75} />
      </Button>
    </form>
  );
}

// -----------------------------------------------------------------------------
// Main chat widget
// -----------------------------------------------------------------------------

const MODES = {
  MENU: "menu",
  BOOK: "book",
  SERVICES: "services",
  HOURS: "hours",
  DONE: "done",
};

export default function ChatAssistant({ waNumber, phoneDial = CLINIC.phoneDial, phoneDisplay = CLINIC.phoneDisplay }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(MODES.MENU);
  const [userMessages, setUserMessages] = useState([]); // strings echoed as UserBubble
  const scrollRef = useRef(null);
  const [showBadge, setShowBadge] = useState(true);
  const [voiceHint, setVoiceHint] = useState("");

  const voice = useVoice({ lang: "en-IN", apiBase: API });

  const waLink = useMemo(
    () => buildWhatsAppLink(waNumber || phoneDial),
    [waNumber, phoneDial],
  );

  // Auto-scroll to latest bubble
  useEffect(() => {
    if (open) requestAnimationFrame(() => {
      scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    });
  }, [open, mode, userMessages.length]);

  // Hide "new" pulse after user opens once
  useEffect(() => {
    if (open) setShowBadge(false);
  }, [open]);

  // Stop TTS / mic when chat is closed
  useEffect(() => {
    if (!open) {
      voice.stopSpeaking();
      if (voice.listening) voice.stop();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Auto-open once per session when the visitor has scrolled ~35% of the page.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem("cs_chat_auto_opened") === "1") return;

    const onScroll = () => {
      const doc = document.documentElement;
      const scrolled = window.scrollY + window.innerHeight;
      const total = doc.scrollHeight;
      const ratio = scrolled / Math.max(total, 1);
      if (ratio > 0.35 || window.scrollY > 700) {
        setOpen(true);
        setShowBadge(false);
        sessionStorage.setItem("cs_chat_auto_opened", "1");
        window.removeEventListener("scroll", onScroll);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    // Fallback: auto-open after 15s of idle browsing
    const timer = setTimeout(() => {
      if (sessionStorage.getItem("cs_chat_auto_opened") !== "1") {
        setOpen(true);
        sessionStorage.setItem("cs_chat_auto_opened", "1");
      }
    }, 15000);
    return () => {
      window.removeEventListener("scroll", onScroll);
      clearTimeout(timer);
    };
  }, []);

  const pushUser = (text) => setUserMessages((m) => [...m, text]);

  const reset = () => {
    setMode(MODES.MENU);
    setUserMessages([]);
    setVoiceHint("");
  };

  // Handle a voice utterance → simple intent routing
  const handleVoiceCommand = (raw) => {
    const text = (raw || "").trim();
    if (!text) return;
    pushUser(`🎙 ${text}`);
    const intent = classifyIntent(text);
    switch (intent) {
      case "book":
        setMode(MODES.BOOK);
        voice.speak("Sure — let's book you in. What is your name?");
        break;
      case "call":
        voice.speak(`Calling ${phoneDisplay}`);
        window.location.href = `tel:${phoneDial}`;
        break;
      case "whatsapp":
        voice.speak("Opening WhatsApp");
        window.open(waLink, "_blank", "noopener");
        break;
      case "services":
        setMode(MODES.SERVICES);
        voice.speak("Here are our treatments. Tap any one to book.");
        break;
      case "hours":
        setMode(MODES.HOURS);
        voice.speak("We are open Monday to Saturday, nine to seven, and Sunday morning.");
        break;
      default:
        setVoiceHint(
          "I didn't quite catch that. You can say things like 'book an appointment', 'call the clinic', 'open WhatsApp', 'show treatments', or 'clinic timings'.",
        );
        voice.speak("Sorry, I didn't catch that. Please try again or tap a button below.");
    }
  };

  // When SpeechRecognition finishes, run the command
  useEffect(() => {
    if (!voice.listening && voice.transcript) {
      handleVoiceCommand(voice.transcript);
      voice.resetTranscript();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voice.listening]);

  const onMicClick = () => {
    if (!voice.supported) return;
    if (voice.listening) voice.stop();
    else voice.start();
  };

  return (
    <>
      {/* Floating trigger */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="btn-chat-toggle"
        aria-label={open ? "Close chat" : "Open chat"}
        className={`fixed z-40 bottom-5 right-5 sm:bottom-6 sm:right-6 group ${open ? "" : ""}`}
      >
        {!open && showBadge && (
          <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-[#D4B27E] border-2 border-[#FAFAF9] animate-pulse z-10" aria-hidden />
        )}
        <span className="absolute inset-0 rounded-full bg-[#2D4A3E] opacity-40 blur-lg group-hover:opacity-60 transition-opacity" aria-hidden />
        <span className="relative flex items-center gap-2 bg-[#2D4A3E] hover:bg-[#21362D] text-white rounded-full pl-3 pr-4 py-3 soft-shadow-lg transition-colors">
          <span className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center">
            {open ? <X className="w-5 h-5" strokeWidth={2} /> : <MessageSquare className="w-5 h-5" strokeWidth={2} />}
          </span>
          {!open && <span className="hidden sm:inline text-sm font-medium">Chat with us</span>}
        </span>
      </button>

      {/* Chat panel */}
      {open && (
        <div
          data-testid="chat-panel"
          className="fixed z-40 inset-x-3 bottom-24 sm:inset-x-auto sm:right-6 sm:bottom-24 sm:w-[400px] max-h-[80vh] rounded-3xl overflow-hidden border border-stone-200 bg-[#FAFAF9] soft-shadow-lg flex flex-col fade-up"
          style={{ animationDelay: "0ms" }}
        >
          {/* Header */}
          <div className="bg-gradient-to-br from-[#2D4A3E] to-[#21362D] text-white px-5 py-4 flex items-center gap-3">
            <div className="relative">
              <div className="w-10 h-10 rounded-full bg-[#D4B27E] text-[#1C221F] font-display font-bold flex items-center justify-center">CS</div>
              <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 border-2 border-[#2D4A3E]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-semibold">City Smiles Assistant</div>
              <div className="text-[11px] text-white/70 flex items-center gap-1">
                <Sparkles className="w-3 h-3" strokeWidth={1.75} /> Usually replies instantly
              </div>
            </div>
            {voice.supported && (
              <button
                type="button"
                onClick={voice.toggleMute}
                aria-label={voice.ttsMuted ? "Enable voice replies" : "Mute voice replies"}
                data-testid="chat-tts-toggle"
                className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
                title={voice.ttsMuted ? "Voice replies off" : "Voice replies on"}
              >
                {voice.ttsMuted
                  ? <VolumeX className="w-4 h-4" strokeWidth={1.75} />
                  : <Volume2 className="w-4 h-4" strokeWidth={1.75} />}
              </button>
            )}
            {mode !== MODES.MENU && (
              <button
                type="button"
                onClick={reset}
                aria-label="Back to menu"
                data-testid="chat-back-menu"
                className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
              >
                <ArrowLeft className="w-4 h-4" strokeWidth={1.75} />
              </button>
            )}
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            <BotBubble delay={0}>{GREETING}</BotBubble>

            {userMessages.map((m, i) => <UserBubble key={i}>{m}</UserBubble>)}

            {mode === MODES.MENU && (
              <BotBubble delay={120}>
                <div>Choose what you need:</div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  <QuickPill
                    testId="chat-quick-book"
                    tone="primary"
                    icon={CalendarIcon}
                    onClick={() => { pushUser("Book an appointment"); setMode(MODES.BOOK); }}
                  >
                    Book appointment
                  </QuickPill>
                  <QuickPill
                    testId="chat-quick-call"
                    icon={Phone}
                    onClick={() => { window.location.href = `tel:${phoneDial}`; }}
                  >
                    Call {phoneDisplay}
                  </QuickPill>
                  <QuickPill
                    testId="chat-quick-whatsapp"
                    tone="wa"
                    onClick={() => window.open(waLink, "_blank", "noopener")}
                  >
                    WhatsApp us
                  </QuickPill>
                  <QuickPill
                    testId="chat-quick-services"
                    onClick={() => { pushUser("Show me the services"); setMode(MODES.SERVICES); }}
                  >
                    Our treatments
                  </QuickPill>
                  <QuickPill
                    testId="chat-quick-hours"
                    icon={Clock}
                    onClick={() => { pushUser("Timings & location"); setMode(MODES.HOURS); }}
                  >
                    Hours & location
                  </QuickPill>
                </div>
              </BotBubble>
            )}

            {mode === MODES.BOOK && (
              <ChatBookingWizard
                onCancel={reset}
                onDone={() => setMode(MODES.DONE)}
              />
            )}

            {mode === MODES.SERVICES && (
              <BotBubble delay={100}>
                <div className="text-xs text-[#57605B] mb-2">Tap any treatment to book it:</div>
                <div className="flex flex-wrap gap-1.5">
                  {SERVICES.map((s) => (
                    <QuickPill
                      key={s.key}
                      testId={`chat-svc-${s.key}`}
                      onClick={() => { pushUser(`I'd like ${s.name}`); setMode(MODES.BOOK); }}
                    >
                      {s.name}
                    </QuickPill>
                  ))}
                </div>
              </BotBubble>
            )}

            {mode === MODES.HOURS && (
              <BotBubble delay={100}>
                <div className="space-y-2 text-xs">
                  <div className="flex items-start gap-2">
                    <Clock className="w-3.5 h-3.5 mt-0.5 text-[#2D4A3E]" strokeWidth={1.75} />
                    <div>
                      <div className="font-medium text-[#1C221F]">Mon – Sat</div>
                      <div className="text-[#57605B]">9:00 AM – 7:00 PM</div>
                      <div className="mt-1 font-medium text-[#1C221F]">Sunday</div>
                      <div className="text-[#57605B]">10:00 AM – 2:00 PM</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-2 pt-2 border-t border-stone-200">
                    <MapPin className="w-3.5 h-3.5 mt-0.5 text-[#2D4A3E]" strokeWidth={1.75} />
                    <div className="text-[#1C221F]">{CLINIC.address}</div>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  <QuickPill
                    testId="chat-hours-directions"
                    icon={MapPin}
                    onClick={() => window.open(CLINIC.googleMapsLink, "_blank", "noopener")}
                  >
                    Get directions
                  </QuickPill>
                  <QuickPill
                    testId="chat-hours-call"
                    icon={Phone}
                    onClick={() => { window.location.href = `tel:${phoneDial}`; }}
                  >
                    Call now
                  </QuickPill>
                </div>
              </BotBubble>
            )}

            {mode === MODES.DONE && (
              <>
                <BotBubble delay={0}>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" strokeWidth={1.75} />
                    <div>
                      <div className="font-medium text-[#1C221F]">You&apos;re booked!</div>
                      <div className="mt-1 text-xs text-[#57605B]">Our team will call {" "}
                        <span className="text-[#1C221F]">to confirm</span> within a few hours.
                        For anything urgent, please call us directly.
                      </div>
                    </div>
                  </div>
                </BotBubble>
                <div className="flex flex-wrap gap-1.5 pl-9">
                  <QuickPill testId="chat-done-call" icon={Phone} onClick={() => { window.location.href = `tel:${phoneDial}`; }}>Call {phoneDisplay}</QuickPill>
                  <QuickPill testId="chat-done-wa" tone="wa" onClick={() => window.open(waLink, "_blank", "noopener")}>WhatsApp</QuickPill>
                  <QuickPill testId="chat-done-menu" onClick={reset}>Back to menu</QuickPill>
                </div>
              </>
            )}

            {/* Voice transcript preview + hint */}
            {voice.listening && (
              <BotBubble delay={0}>
                <div className="flex items-center gap-2 text-xs text-[#57605B]">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-rose-500" />
                  </span>
                  Listening… try &ldquo;book an appointment&rdquo;, &ldquo;call&rdquo;, &ldquo;whatsapp&rdquo;, &ldquo;treatments&rdquo; or &ldquo;timings&rdquo;.
                </div>
              </BotBubble>
            )}
            {voice.transcribing && (
              <BotBubble delay={0}>
                <div className="flex items-center gap-2 text-xs text-[#57605B]">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.75} />
                  Transcribing your message…
                </div>
              </BotBubble>
            )}
            {voice.error && !voice.listening && !voice.transcribing && (
              <BotBubble delay={0}>
                <div className="text-xs text-rose-600" data-testid="chat-voice-error">{voice.error}</div>
                {(voice.inIframe || voice.permission === "denied") && (
                  <button
                    type="button"
                    onClick={voice.openTopLevel}
                    data-testid="chat-open-full-site"
                    className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium bg-[#2D4A3E] text-white rounded-full px-3 py-1.5 hover:bg-[#21362D] transition-colors"
                  >
                    Open full site in new tab →
                  </button>
                )}
              </BotBubble>
            )}
            {voiceHint && !voice.listening && (
              <BotBubble delay={0}>
                <div className="text-xs text-[#57605B]">{voiceHint}</div>
              </BotBubble>
            )}
          </div>

          {/* Footer / voice input */}
          <div className="px-3 py-2.5 bg-white border-t border-stone-200 flex items-center gap-2">
            {voice.supported ? (
              <button
                type="button"
                onClick={onMicClick}
                disabled={voice.transcribing}
                data-testid="chat-mic-toggle"
                aria-label={voice.listening ? "Stop voice input" : "Start voice input"}
                className={cn(
                  "shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all border disabled:opacity-60",
                  voice.listening
                    ? "bg-rose-500 text-white border-rose-500 animate-pulse"
                    : "bg-[#2D4A3E] text-white border-[#2D4A3E] hover:bg-[#21362D]"
                )}
                title={voice.listening ? "Stop" : "Speak to book / call / WhatsApp"}
              >
                {voice.transcribing
                  ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />
                  : voice.listening
                    ? <MicOff className="w-4 h-4" strokeWidth={2} />
                    : <Mic className="w-4 h-4" strokeWidth={2} />}
              </button>
            ) : (
              <div
                data-testid="chat-mic-unsupported"
                className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-stone-100 text-stone-400 border border-stone-200"
                title="Voice input isn't supported in this browser"
              >
                <MicOff className="w-4 h-4" strokeWidth={2} />
              </div>
            )}
            <div className="flex-1 min-w-0 flex flex-col leading-tight">
              <span className="text-[11px] text-[#1C221F] font-medium">
                {voice.transcribing
                  ? "Transcribing…"
                  : voice.listening
                    ? "Listening — tap mic to stop"
                    : voice.inIframe
                      ? "Voice needs the full site"
                      : voice.supported
                        ? "Tap the mic and speak"
                        : "Use the buttons above ↑"}
              </span>
              {voice.inIframe ? (
                <button
                  type="button"
                  onClick={voice.openTopLevel}
                  data-testid="chat-mic-open-full"
                  className="text-[10px] text-[#2D4A3E] hover:underline font-medium text-left"
                >
                  Open full site in new tab →
                </button>
              ) : (
                <a
                  href={waLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="chat-footer-wa"
                  className="text-[10px] text-[#25D366] hover:underline font-medium"
                >
                  Or chat on WhatsApp →
                </a>
              )}
            </div>
            <div className="text-[10px] text-[#57605B]/70 shrink-0 hidden sm:block">
              City Smiles · {new Date().getFullYear()}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
